/** Fixed scalar sound counters. No IDs, market names, settings values or audio. */
export const SOUND_COUNTER_KEYS = ['received', 'preSession', 'notRecent', 'disabled', 'belowMinimum',
  'queued', 'scheduled', 'blocked', 'muted', 'fenced', 'failed', 'partial',
  'coordUnknown', 'coordCapacity', 'coordSuppressed', 'coordExpired'] as const;
export type SoundCounters = Record<typeof SOUND_COUNTER_KEYS[number], number>;
function validSoundCounters(value: unknown): value is SoundCounters {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const v = value as Record<string, unknown>;
  return Object.keys(v).length === SOUND_COUNTER_KEYS.length && SOUND_COUNTER_KEYS.every(key =>
    Number.isSafeInteger(v[key]) && (v[key] as number) >= 0 && (v[key] as number) <= 1_000_000);
}

/** #1028 — fixed scalar queue-lane counters: stream/poll state, frame counts and
 * how many orders show a queue. No ids, tickers, prices or queue values. */
export const QUEUE_COUNTER_KEYS = ['streamActive', 'pollArmed', 'queueFresh', 'lastFrameAgeMs',
  'framesReceived', 'framesDropped', 'orders', 'ordersWithQueue'] as const;
export type QueueCounters = Record<typeof QUEUE_COUNTER_KEYS[number], number>;
function validQueueCounters(value: unknown): value is QueueCounters {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const v = value as Record<string, unknown>;
  return Object.keys(v).length === QUEUE_COUNTER_KEYS.length && QUEUE_COUNTER_KEYS.every(key =>
    Number.isSafeInteger(v[key]) && (v[key] as number) >= 0 && (v[key] as number) <= 1_000_000_000);
}

/** Pure, shared browser/relay privacy contract. No text or trading payloads. */
export interface BrowserSample {
  version: 1;
  session: string;
  sequence: number;
  build: string;
  wallMs: number;
  elapsedMs: number;
  pageAgeMs: number;
  route: 'game' | 'positions' | 'settings' | 'other';
  hidden: boolean;
  heapUsed: number | null;
  heapTotal: number | null;
  heapLimit: number | null;
  elements: number;
  canvases: number;
  canvasPixels: number;
  domTruncated: boolean;
  sound?: SoundCounters;
  queue?: QueueCounters;
}
const numbers = ['sequence', 'wallMs', 'elapsedMs', 'pageAgeMs', 'elements', 'canvases', 'canvasPixels'] as const;
const heaps = ['heapUsed', 'heapTotal', 'heapLimit'] as const;
const keys = new Set(['version', 'session', 'build', 'route', 'hidden', 'domTruncated', ...numbers, ...heaps]);

export function validBrowserSample(value: unknown): value is BrowserSample {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const v = value as Record<string, unknown>;
  if (Object.keys(v).length !== keys.size + (v.sound === undefined ? 0 : 1) + (v.queue === undefined ? 0 : 1)
    || Object.keys(v).some((k) => k !== 'sound' && k !== 'queue' && !keys.has(k))) return false;
  if (v.sound !== undefined && !validSoundCounters(v.sound)) return false;
  if (v.queue !== undefined && !validQueueCounters(v.queue)) return false;
  if (v.version !== 1 || typeof v.session !== 'string' || !/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/.test(v.session)) return false;
  if (typeof v.build !== 'string' || !/^(unknown|[a-f0-9]{7,40})$/.test(v.build)) return false;
  if (typeof v.route !== 'string' || !['game', 'positions', 'settings', 'other'].includes(v.route)) return false;
  if (typeof v.hidden !== 'boolean' || typeof v.domTruncated !== 'boolean') return false;
  for (const key of numbers) if (!Number.isSafeInteger(v[key]) || (v[key] as number) < 0) return false;
  for (const key of heaps) if (v[key] !== null && (!Number.isSafeInteger(v[key]) || (v[key] as number) < 0)) return false;
  return (v.sequence as number) <= 60 && (v.sequence as number) >= 1
    && (v.elapsedMs as number) < 30 * 60_000 && (v.elements as number) <= 10_000
    && (v.canvases as number) <= (v.elements as number);
}
